import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, CardHeader, ErrorNotice, LoadingState, Panel } from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { saveSystemSettings } from '@/lib/events';
import {
  APP_FEATURES_QUERY_KEY,
  APP_FEATURES_SETTING,
  readAppFeatures,
  type AppFeatureFlags,
  type AppFeatureKey,
} from '@/lib/app-features';

/**
 * Which newer features the team sees. Everything is on until an admin turns
 * it off; off hides the feature for everyone, admins included, and keeps its
 * data so switching it back on picks up where it left off.
 */
export function FeaturesTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const features = useQuery({
    queryKey: APP_FEATURES_QUERY_KEY,
    queryFn: ({ signal }) => readAppFeatures(signal),
  });
  const [draft, setDraft] = useState<AppFeatureFlags | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (flags: AppFeatureFlags) =>
      saveSystemSettings([{ name: APP_FEATURES_SETTING, value: flags }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: APP_FEATURES_QUERY_KEY });
      setDraft(null);
      setSaved(true);
    },
  });

  if (features.isPending) return <LoadingState label="Loading features…" />;
  if (features.isError) {
    return <ErrorNotice title="Features unavailable" message={errorMessage(features.error)} />;
  }
  const { catalog, enabled } = features.data;
  const values = draft ?? enabled;
  const isOn = (key: AppFeatureKey): boolean => values[key] ?? true;
  const dirty = draft !== null && catalog.some((feature) => isOn(feature.key) !== (enabled[feature.key] ?? true));

  function set(key: AppFeatureKey, on: boolean): void {
    setSaved(false);
    setDraft({ ...values, [key]: on });
  }

  return (
    <div className="mt-6 space-y-5">
      <Panel className="overflow-hidden">
        <div className="p-5 pb-4">
          <CardHeader
            className="mb-0"
            title="Features"
            description="Turn off anything the team isn't ready to use yet. An unticked feature disappears for everyone, admins included: its screens, BESTie's tools for it and its What's new notes. Nothing is deleted, so ticking it again brings it back as it was."
          />
        </div>
        <ul className="divide-y divide-portal-line border-t border-portal-line">
          {catalog.map((feature) => {
            const id = `app-feature-${feature.key}`;
            return (
              <li key={feature.key} className="flex items-start gap-3 px-5 py-3">
                <Checkbox
                  id={id}
                  checked={isOn(feature.key)}
                  disabled={save.isPending}
                  onCheckedChange={(state) => set(feature.key, state === true)}
                  className="mt-0.5"
                />
                <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                  <span className="block text-portal-small font-bold">{feature.label}</span>
                  <span className="block text-portal-micro text-portal-muted">{feature.description}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </Panel>

      {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}

      <div className="flex items-center gap-3">
        <Button
          disabled={save.isPending || !dirty}
          onClick={() => save.mutate(Object.fromEntries(catalog.map((feature) => [feature.key, isOn(feature.key)])))}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" disabled={save.isPending || !dirty} onClick={() => setDraft(null)}>
          Reset
        </Button>
        {saved && !dirty ? <span className="text-portal-micro font-bold text-emerald-600">Saved</span> : null}
      </div>
    </div>
  );
}
